package link.erpango.quip.features.myspareparts

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.data.remote.repository.user.IUserRepository
import link.erpango.quip.domain.entities.DShareResult
import link.erpango.quip.domain.entities.DUser
import link.erpango.quip.domain.usecases.IMySparePartsUseCase

/**
 * Estado de UI para la pantalla de compartir un repuesto personal ("Mis repuestos").
 */
data class ShareMySparePartUiState(
    val partId: Long,
    /** True mientras se carga la lista de usuarios. */
    val isLoadingUsers: Boolean = true,
    /** Error al cargar los usuarios (permite reintentar). */
    val usersLoadError: String? = null,
    val searchQuery: String = "",
    /** Usuarios visibles tras aplicar el filtro de búsqueda (ya sin el usuario actual). */
    val visibleUsers: List<DUser> = emptyList(),
    /** Usuario seleccionado para compartir. */
    val selectedUser: DUser? = null,
    val isSharing: Boolean = false,
    /** Mensaje a mostrar en snackbar: sentinel o mensaje crudo del backend. */
    val snackbarMessage: String? = null,
    /** 201: ID de la copia creada para el usuario destino (diálogo de éxito). */
    val sharedCopyId: Long? = null,
    /** Usuario que recibió la copia (para el mensaje del diálogo de éxito). */
    val sharedWithUser: DUser? = null,
    /** 409: usuario que ya tenía una copia de este repuesto (diálogo solo-informativo). */
    val alreadySharedUser: DUser? = null
)

/**
 * ViewModel para compartir un repuesto personal con otro usuario.
 *
 * Carga la lista de usuarios del backend, permite buscarlos en el cliente por nombre/email
 * y dispara [IMySparePartsUseCase.shareMySparePart], que crea una copia independiente del
 * repuesto en la cuenta destino (201) o informa de que ya existía copia (409).
 */
class ShareMySparePartViewModel(
    private val mySparePartsUseCase: IMySparePartsUseCase,
    private val userRepository: IUserRepository,
    partId: Long,
    /** ID del usuario con sesión activa; se excluye de la lista de destinos. */
    private val currentUserId: String? = null
) : ViewModel() {

    companion object {
        const val SEARCH_DEBOUNCE_MS = 300L
        /** Sentinel de snackbar/error: error genérico sin mensaje del backend. */
        const val SNACKBAR_GENERIC_ERROR = "generic_error"
    }

    private val _uiState = MutableStateFlow(ShareMySparePartUiState(partId = partId))
    val uiState: StateFlow<ShareMySparePartUiState> = _uiState.asStateFlow()

    /** Usuarios cargados del backend, ya sin el usuario actual. */
    private var loadedUsers: List<DUser> = emptyList()

    // Job de la última búsqueda, para cancelarla y no aplicar resultados obsoletos.
    private var searchJob: Job? = null

    init {
        loadUsers()
    }

    // ------------------------------------------------------------------ carga de usuarios

    /** Carga todos los usuarios (GET "api/users/") y excluye al usuario actual. */
    fun loadUsers() {
        _uiState.update { it.copy(isLoadingUsers = true, usersLoadError = null) }
        viewModelScope.launch {
            when (val result = userRepository.getUsers()) {
                is DResponse.Success -> {
                    loadedUsers = result.data.filter { user -> user.id != currentUserId }
                    _uiState.update { state ->
                        state.copy(
                            isLoadingUsers = false,
                            visibleUsers = filterUsers(state.searchQuery)
                        )
                    }
                }
                else -> _uiState.update {
                    it.copy(
                        isLoadingUsers = false,
                        usersLoadError = (result as? DResponse.Error)?.error
                            ?.takeIf { message -> message.isNotBlank() } ?: SNACKBAR_GENERIC_ERROR
                    )
                }
            }
        }
    }

    // ------------------------------------------------------------------ búsqueda en cliente

    /**
     * Búsqueda con debounce de [SEARCH_DEBOUNCE_MS] ms sobre nombre/email.
     * Cada cambio cancela el filtrado pendiente para no aplicar resultados obsoletos.
     */
    fun onSearchQueryChange(query: String) {
        _uiState.update { it.copy(searchQuery = query, snackbarMessage = null) }
        searchJob?.cancel()
        searchJob = viewModelScope.launch {
            if (query.isNotBlank()) delay(SEARCH_DEBOUNCE_MS)
            _uiState.update { it.copy(visibleUsers = filterUsers(query)) }
        }
    }

    private fun filterUsers(query: String): List<DUser> {
        val q = query.trim().lowercase()
        if (q.isEmpty()) return loadedUsers
        return loadedUsers.filter { user ->
            user.name?.contains(q, ignoreCase = true) == true ||
                user.email?.contains(q, ignoreCase = true) == true
        }
    }

    // ------------------------------------------------------------------ selección y compartir

    /** Alterna la selección del usuario destino. */
    fun selectUser(user: DUser) {
        _uiState.update { state ->
            state.copy(
                selectedUser = if (state.selectedUser == user) null else user,
                snackbarMessage = null
            )
        }
    }

    /** Comparte el repuesto con el usuario seleccionado (crea una copia independiente). */
    fun confirmShare() {
        val state = _uiState.value
        if (state.isSharing || state.sharedCopyId != null) return
        val targetUser = state.selectedUser ?: return
        val targetUserId = targetUser.id?.toIntOrNull()
        if (targetUserId == null) {
            _uiState.update { it.copy(snackbarMessage = SNACKBAR_GENERIC_ERROR) }
            return
        }

        _uiState.update { it.copy(isSharing = true, snackbarMessage = null) }
        viewModelScope.launch {
            when (val result = mySparePartsUseCase.shareMySparePart(state.partId, targetUserId)) {
                is DResponse.Success -> when (val shareResult = result.data) {
                    is DShareResult.Shared -> _uiState.update {
                        it.copy(
                            isSharing = false,
                            sharedCopyId = shareResult.sharedSparePartId,
                            sharedWithUser = targetUser
                        )
                    }
                    is DShareResult.AlreadyShared -> _uiState.update {
                        it.copy(isSharing = false, alreadySharedUser = targetUser)
                    }
                }
                else -> _uiState.update {
                    it.copy(
                        isSharing = false,
                        snackbarMessage = (result as? DResponse.Error)?.error
                            ?.takeIf { message -> message.isNotBlank() } ?: SNACKBAR_GENERIC_ERROR
                    )
                }
            }
        }
    }

    /**
     * Reinicia las banderas de resultado una vez que el diálogo se ha cerrado,
     * para que reabrir la ruta no dispare otro diálogo automático.
     */
    fun clearShareResults() {
        _uiState.update { it.copy(sharedCopyId = null, sharedWithUser = null, alreadySharedUser = null) }
    }
}
